// Client for the "Ask this site" generation endpoints.
//
// The EdgeOne DeepSeek endpoint is deployed and ready, while client routing to
// it is currently paused. The fallback machinery stays intact so it can be
// enabled again without rebuilding the backend.

const DEFAULT_ENDPOINT = process.env.VUE_APP_ASK_ENDPOINT || 'https://ask-henry.whenry6688.workers.dev';
const DEFAULT_FALLBACK_ENDPOINTS = process.env.VUE_APP_ASK_EDGEONE_ENDPOINTS || process.env.VUE_APP_ASK_EDGEONE_ENDPOINT || 'https://personalpage.edgeone.cool/api/ask,https://ask-cn.henryw.me/api/ask';
const PRIMARY_PROBE_TIMEOUT_MS = Number(process.env.VUE_APP_ASK_PRIMARY_PROBE_TIMEOUT_MS || 900);
const ASK_POST_TIMEOUT_MS = Number(process.env.VUE_APP_ASK_POST_TIMEOUT_MS || 18000);
const FALLBACK_POST_RETRIES = Number(process.env.VUE_APP_ASK_FALLBACK_POST_RETRIES || 3);
const FALLBACK_RETRY_DELAY_MS = Number(process.env.VUE_APP_ASK_FALLBACK_RETRY_DELAY_MS || 300);
const FALLBACK_REGION_MODE = process.env.VUE_APP_ASK_FALLBACK_REGION || 'auto';
const MAINLAND_TIME_ZONES = new Set([
  'Asia/Shanghai',
  'Asia/Chongqing',
  'Asia/Harbin',
  'Asia/Urumqi',
  'Asia/Kashgar',
]);

function cleanEndpoint(value) {
  return String(value || '').trim().replace(/\/+$/, '');
}

function parseEndpointList(value) {
  const raw = String(value || '').trim();
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.map(cleanEndpoint).filter(Boolean);
  } catch {
    // Fall back to comma-separated parsing.
  }
  return raw.split(',').map(cleanEndpoint).filter(Boolean);
}

function endpoints() {
  try {
    const explicitList = parseEndpointList(localStorage.getItem('askEndpoints'));
    if (explicitList.length) return [...new Set(explicitList)];

    const primary = cleanEndpoint(localStorage.getItem('askEndpoint') || DEFAULT_ENDPOINT);
    const fallbackList = parseEndpointList(localStorage.getItem('askFallbackEndpoints'));
    const singleFallback = cleanEndpoint(localStorage.getItem('askFallbackEndpoint'));
    const fallbacks = fallbackList.length ? fallbackList : parseEndpointList(singleFallback || DEFAULT_FALLBACK_ENDPOINTS);
    return [...new Set([primary, ...fallbacks].filter(Boolean))];
  } catch {
    return [...new Set([cleanEndpoint(DEFAULT_ENDPOINT), ...parseEndpointList(DEFAULT_FALLBACK_ENDPOINTS)].filter(Boolean))];
  }
}

export function askEndpoints() {
  return endpoints();
}

export function askConfigured() {
  return endpoints().length > 0;
}

// The endpoint telemetry posts to (same Worker, /t route). Exported here so the
// telemetry module has a single source of truth for the base URL.
export function primaryEndpoint() {
  return endpoints()[0] || '';
}

function fallbackRegionMode() {
  try {
    return String(localStorage.getItem('askFallbackRegion') || FALLBACK_REGION_MODE).trim().toLowerCase();
  } catch {
    return FALLBACK_REGION_MODE;
  }
}

function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

function shouldProbePrimaryForFallback() {
  const mode = fallbackRegionMode();
  if (['cn', 'china', 'mainland', 'edgeone'].includes(mode)) return true;
  if (['global', 'cloudflare', 'off', 'false'].includes(mode)) return false;
  return MAINLAND_TIME_ZONES.has(browserTimeZone());
}

function fallbackEnabled() {
  if (EDGEONE_FALLBACK_RETIRED) return false;
  return shouldProbePrimaryForFallback();
}

const EDGEONE_FALLBACK_RETIRED = true;

// Best-effort guess of whether the visitor is in mainland China, from the browser
// time zone or an explicit override.
export function isMainlandRegion() {
  const mode = fallbackRegionMode();
  if (['cn', 'china', 'mainland'].includes(mode)) return true;
  if (['global', 'cloudflare', 'off', 'false'].includes(mode)) return false;
  return MAINLAND_TIME_ZONES.has(browserTimeZone());
}

export function askErrorMessage() {
  return isMainlandRegion()
    ? 'Ask is currently unavailable in your region. 该功能在当前区域暂不可用。'
    : 'Something went wrong. Please try again.';
}

function timeoutSignal(ms) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return { signal: controller.signal, cancel: () => clearTimeout(timer) };
}

async function probeEndpoint(url) {
  const probe = timeoutSignal(PRIMARY_PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      method: 'OPTIONS',
      cache: 'no-store',
      signal: probe.signal,
    });
    return res.ok || res.status < 500;
  } catch {
    return false;
  } finally {
    probe.cancel();
  }
}

async function endpointOrder() {
  const list = endpoints();
  if (list.length <= 1) return list;
  const primary = list[0];
  const fallbacks = list.slice(1);
  if (!fallbackEnabled()) return [primary];

  const primaryReachable = await probeEndpoint(primary);
  if (primaryReachable) return [primary, ...fallbacks];

  return fallbacks.length ? [...fallbacks, primary] : [primary];
}

async function postAsk(url, question, contexts, meta) {
  const timeout = timeoutSignal(ASK_POST_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // `meta` carries the event/session ids so the Worker's row can be joined
      // with the browser-side retrieval telemetry. Older Workers ignore it.
      body: JSON.stringify({ question, contexts, meta }),
      signal: timeout.signal,
    });
    if (!res.ok || !res.body) throw new Error(`ask failed at ${url}: ${res.status}`);
    return res;
  } finally {
    timeout.cancel();
  }
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Demo trigger: any Ask input containing <develop> skips the API and streams a
// realistic preset answer instead, so the full answer experience (thinking
// state, settling answer, cited sources, follow-ups) can be reviewed offline.
export function isDevelopMode(text) {
  return /<develop>/i.test(String(text || ''));
}

const DEVELOP_ASK_RESPONSE = [
  'I explore the site as a working archive, so this answer is grounded in passages retrieved from Henry’s own writing [1]. ',
  'Henry builds systems that help people explore a personal archive with AI [2]. The site layers lexical and semantic search, so exact phrases and loose conceptual questions both find the original material, and then uses retrieval-augmented generation to answer with citations back to the source pages. ',
  'The View mode goes one step further: it composes a focused reading path that keeps the original sources visible while connecting material across projects, research, travel, and fragments [3].',
  '\n\n[[VIEW]]\noffer\ntitle: Explore this in a View\ndescription: Compose a focused reading path across the site’s projects, research, and fragments.',
  '\n\n[[FOLLOWUPS]]\n- How does search work on this site?\n- What is a View?\n- What research is Henry working on?',
].join('');

function developAskChunks() {
  // Small word-sized chunks with a natural pause between sentences, so the
  // stream reads like a live answer rather than a pasted block.
  const parts = DEVELOP_ASK_RESPONSE.match(/\S+\s*/g) || [];
  const chunks = [];
  let buffer = '';
  for (const part of parts) {
    buffer += part;
    if (buffer.length >= 26 || /[.!?]\s*$/.test(buffer)) {
      chunks.push(buffer);
      buffer = '';
    }
  }
  if (buffer) chunks.push(buffer);
  return chunks;
}

// Break a received token into small display-sized pieces so answers settle in
// gradually even when the provider returns a whole paragraph at once.
function smoothChunks(text) {
  const value = String(text || '');
  if (value.length <= 28) return [value];
  const parts = value.match(/\S+\s*/g) || [value];
  const chunks = [];
  let buffer = '';
  for (const part of parts) {
    buffer += part;
    if (buffer.length >= 28) {
      chunks.push(buffer);
      buffer = '';
    }
  }
  if (buffer) chunks.push(buffer);
  return chunks.length ? chunks : [value];
}

async function postAskWithRetries(url, question, contexts, retries, meta) {
  let lastError = null;
  for (let attempt = 0; attempt < retries; attempt += 1) {
    try {
      return await postAsk(url, question, contexts, meta);
    } catch (err) {
      lastError = err;
      if (attempt + 1 < retries) await wait(FALLBACK_RETRY_DELAY_MS);
    }
  }
  throw lastError || new Error(`ask failed at ${url}`);
}

// Async generator yielding answer text chunks as they stream in. Parses the
// Workers AI SSE format: lines of `data: {"response":"..."}`, ending at `[DONE]`.
// `options.meta` is echoed to the Worker for log joining; `options.onFirstToken`
// fires once, when the first token arrives, so callers can measure TTFT.
export async function* askStream(question, contexts, options = {}) {
  const { meta, onFirstToken } = options;
  if (isDevelopMode(question)) {
    const chunks = developAskChunks();
    for (let i = 0; i < chunks.length; i += 1) {
      if (i === 0) {
        try {
          onFirstToken?.();
        } catch {
          // a telemetry hook must never break the stream
        }
      }
      yield chunks[i];
      await wait(34);
    }
    return;
  }
  const candidates = await endpointOrder();
  if (!candidates.length) throw new Error('ask endpoint not configured');
  const primary = endpoints()[0];

  let res = null;
  let lastError = null;
  for (const url of candidates) {
    try {
      const retries = url === primary ? 1 : Math.max(1, FALLBACK_POST_RETRIES);
      res = await postAskWithRetries(url, question, contexts, retries, meta);
      break;
    } catch (err) {
      lastError = err;
    }
  }
  if (!res) throw lastError || new Error('ask failed');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let first = true;

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const events = buffer.split('\n\n');
    buffer = events.pop() || ''; // keep the trailing partial event
    for (const evt of events) {
      const line = evt.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      const data = line.slice(5).trim();
      if (data === '[DONE]') return;
        try {
          const json = JSON.parse(data);
          if (json.response) {
            if (first) {
              first = false;
              try {
                onFirstToken?.();
              } catch {
                // a telemetry hook must never break the stream
              }
            }
            for (const piece of smoothChunks(json.response)) {
              yield piece;
              await wait(22);
            }
          }
      } catch {
        // ignore keep-alives / non-JSON lines
      }
    }
  }
}
